import React, { useEffect, useRef, useState } from 'react';
import { getAdminProspectCampaignMailingPublicResponse } from '../../services/admin/prospectCampaignMailingPublicResponse.js';
import { formatCadCentsForDisplay } from '../../utils/publicCadMoney.js';
import {
  adminProspectPublicResponseCopy,
  adminProspectPublicResponseIntentLabel,
} from './adminProspectCampaignDisplay.js';
import {
  parseFocusedPublicResponseIdentity,
  selectFocusedPublicResponseIntent,
  shouldApplyFocusedPublicResponseResult,
} from './prospectFocusedPublicResponse.js';

export {
  parseFocusedPublicResponseIdentity,
  selectFocusedPublicResponseIntent,
  shouldApplyFocusedPublicResponseResult,
} from './prospectFocusedPublicResponse.js';

const UNIT_MIX_LABEL_KEYS = Object.freeze({
  oneAndHalf: 'unitOneAndHalf',
  twoAndHalf: 'unitTwoAndHalf',
  threeAndHalf: 'unitThreeAndHalf',
  fourAndHalf: 'unitFourAndHalf',
  fiveAndHalf: 'unitFiveAndHalf',
  sixAndHalfPlus: 'unitSixAndHalfPlus',
  commercial: 'unitCommercial',
  other: 'unitOther',
});

function formatAdminMoneyOrUnknown(cents, language, unknownLabel) {
  if (cents === null) return unknownLabel;
  return formatCadCentsForDisplay(cents, language) || unknownLabel;
}

function FocusedPublicResponseDetail({ item, language, responseCopy }) {
  const financial = item.financialSnapshot;
  const profile = item.propertyProfile;
  return (
    <div className="rounded-lg border border-slate-200 p-3 text-sm">
      <p className="font-semibold text-slate-900">
        {adminProspectPublicResponseIntentLabel(item.intent, language)}
      </p>
      <div className="mt-2 space-y-1 text-slate-700">
        {item.submittedAt ? (
          <p>
            <span className="font-medium">{responseCopy.submittedAt}:</span> {item.submittedAt}
          </p>
        ) : null}
        {typeof item.schemaVersion === 'number' ? (
          <p>
            <span className="font-medium">{responseCopy.schemaVersion}:</span> {item.schemaVersion}
          </p>
        ) : null}
        {item.language ? (
          <p>
            <span className="font-medium">{responseCopy.languageLabel}:</span>{' '}
            {item.language.toUpperCase()}
          </p>
        ) : null}
      </div>
      <div className="mt-3">
        <p className="font-semibold text-slate-900">{responseCopy.contact}</p>
        <p className="mt-1">{item.contact?.fullName}</p>
        <p>{item.contact?.email}</p>
        {item.contact?.phone ? <p>{item.contact.phone}</p> : null}
        {item.intent === 'property_sale' && item.ownershipAttested ? (
          <p className="mt-1 text-xs text-slate-600">{responseCopy.ownershipAttested}</p>
        ) : null}
      </div>
      {item.intent === 'property_value' && item.schemaVersion === 3 && financial ? (
        <div className="mt-3 space-y-1">
          <p className="font-semibold text-slate-900">{responseCopy.financial}</p>
          <p>
            {responseCopy.grossAnnualIncome}:{' '}
            {formatAdminMoneyOrUnknown(financial.grossAnnualIncomeCents, language, responseCopy.unknown)}
          </p>
          <p>
            {responseCopy.municipalTaxes}:{' '}
            {formatAdminMoneyOrUnknown(
              financial.expenses?.municipalTaxesCents,
              language,
              responseCopy.unknown,
            )}
          </p>
          <p>
            {responseCopy.schoolTaxes}:{' '}
            {formatAdminMoneyOrUnknown(
              financial.expenses?.schoolTaxesCents,
              language,
              responseCopy.unknown,
            )}
          </p>
          <p>
            {responseCopy.insurance}:{' '}
            {formatAdminMoneyOrUnknown(financial.expenses?.insuranceCents, language, responseCopy.unknown)}
          </p>
          <p>
            {responseCopy.heatingElectricity}:{' '}
            {formatAdminMoneyOrUnknown(
              financial.expenses?.heatingElectricityCents,
              language,
              responseCopy.unknown,
            )}
          </p>
          <p>
            {responseCopy.otherExpenses}:{' '}
            {formatAdminMoneyOrUnknown(
              financial.expenses?.otherExpensesCents,
              language,
              responseCopy.unknown,
            )}
          </p>
          {financial.otherExpensesDescription ? (
            <p>
              {responseCopy.otherExpensesDescription}: {financial.otherExpensesDescription}
            </p>
          ) : null}
        </div>
      ) : null}
      {item.intent === 'property_value' && item.schemaVersion === 3 && profile ? (
        <div className="mt-3 space-y-1">
          <p className="font-semibold text-slate-900">{responseCopy.building}</p>
          <p>
            {responseCopy.constructionYear}:{' '}
            {profile.constructionYearUnknown
              ? responseCopy.constructionYearUnknown
              : profile.constructionYear ?? responseCopy.unknown}
          </p>
          {profile.unitMix
            ? Object.entries(profile.unitMix)
                .filter(([, count]) => Number.isInteger(count) && count > 0)
                .map(([key, count]) => (
                  <p key={key}>
                    {responseCopy[UNIT_MIX_LABEL_KEYS[key]] || key}: {count}
                  </p>
                ))
            : null}
          {profile.declaredUnitTotal != null ? (
            <p>
              {responseCopy.declaredUnits}: {profile.declaredUnitTotal}
            </p>
          ) : null}
          {profile.importedNumberOfUnits != null ? (
            <p>
              {responseCopy.importedUnits}: {profile.importedNumberOfUnits}
            </p>
          ) : null}
          {profile.unitCountMismatchConfirmed ? (
            <p className="font-medium text-amber-800">{responseCopy.unitMismatch}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Exact public-response focus panel for Prospect 360.
 * Does not mark processed. HashRouter-safe (no hash fragments).
 */
export default function ProspectFocusedPublicResponsePanel({
  campaignId,
  mailingId,
  intent,
  visitId,
  authUid,
  language = 'fr',
  t,
}) {
  const responseCopy = adminProspectPublicResponseCopy(language);
  const mountedRef = useRef(true);
  const authUidRef = useRef(authUid ? String(authUid) : '');
  const visitIdRef = useRef(visitId);
  const requestIdRef = useRef(0);

  const [status, setStatus] = useState('loading');
  const [item, setItem] = useState(null);

  authUidRef.current = authUid ? String(authUid) : '';
  visitIdRef.current = visitId;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const identity = parseFocusedPublicResponseIdentity({ campaignId, mailingId, intent });
    if (!identity) {
      setStatus('unavailable');
      setItem(null);
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    const requestVisitId = visitIdRef.current;
    const requestAuthUid = authUidRef.current;
    setStatus('loading');
    setItem(null);

    let cancelled = false;
    (async () => {
      try {
        const data = await getAdminProspectCampaignMailingPublicResponse({
          campaignId: identity.campaignId,
          mailingId: identity.mailingId,
        });
        if (cancelled) return;
        if (
          !shouldApplyFocusedPublicResponseResult({
            mounted: mountedRef.current,
            requestId,
            activeRequestId: requestIdRef.current,
            requestVisitId,
            activeVisitId: visitIdRef.current,
            requestAuthUid,
            activeAuthUid: authUidRef.current,
          })
        ) {
          return;
        }
        const selected = selectFocusedPublicResponseIntent(data?.responses, identity.intent);
        if (selected.status !== 'ready') {
          setItem(null);
          setStatus('unavailable');
          return;
        }
        setItem(selected.item);
        setStatus('ready');
      } catch {
        if (cancelled) return;
        if (
          !shouldApplyFocusedPublicResponseResult({
            mounted: mountedRef.current,
            requestId,
            activeRequestId: requestIdRef.current,
            requestVisitId,
            activeVisitId: visitIdRef.current,
            requestAuthUid,
            activeAuthUid: authUidRef.current,
          })
        ) {
          return;
        }
        setItem(null);
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [campaignId, mailingId, intent, visitId, authUid]);

  const title = t('admin.prospection.focusedPublicResponse.title');

  return (
    <section
      className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4"
      aria-label={title}
    >
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {status === 'loading' ? (
        <p className="mt-2 text-sm text-slate-600">
          {t('admin.prospection.focusedPublicResponse.loading')}
        </p>
      ) : null}
      {status === 'error' ? (
        <p className="mt-2 text-sm text-rose-800" role="alert">
          {t('admin.prospection.focusedPublicResponse.error')}
        </p>
      ) : null}
      {status === 'unavailable' ? (
        <p className="mt-2 text-sm text-slate-600" role="status">
          {t('admin.prospection.focusedPublicResponse.unavailable')}
        </p>
      ) : null}
      {status === 'ready' && item ? (
        <div className="mt-3">
          <FocusedPublicResponseDetail item={item} language={language} responseCopy={responseCopy} />
        </div>
      ) : null}
    </section>
  );
}
